import React from 'react';
import { Icon, Button } from '@tviklax/ui-components';
import styled from 'styled-components';
import { OrderFormData } from './types';

const ReviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const ConfidenceAlert = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: #92400e;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }

  /* Reset span for the address field on mobile */
  .address-field {
    grid-column: span 2;
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      grid-column: span 1;
    }
  }
`;

const ReviewField = styled.div<{ $lowConfidence?: boolean }>`
  padding: ${({ theme }) => theme.spacing[4]};
  border: 1px solid ${({ $lowConfidence, theme }) => ($lowConfidence ? '#f59e0b' : theme.colors.gray200)};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ $lowConfidence }) => ($lowConfidence ? '#fff7ed' : 'white')};
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FieldLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray500};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
`;

const FieldValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray800};
  font-weight: 500;
`;

const ConfidenceBadge = styled.span`
  color: #f59e0b;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
`;

interface ReviewExtractedDataProps {
    data: Partial<OrderFormData> & { confidence?: Record<string, number> };
    onConfirm: () => void;
  onEdit?: () => void;
  secondaryActionLabel?: string;
}

import { StatusBadge } from './styles';

const ReviewActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
`;

export const ReviewExtractedData: React.FC<ReviewExtractedDataProps> = ({
  data,
  onConfirm,
  onEdit,
  secondaryActionLabel,
}) => {
    const isLowConfidence = (field: string) => (data.confidence?.[field] || 1) < 0.85;

    const renderStatus = (field: string, value?: string) => {
        if (!value || value === 'Not found') return <StatusBadge $status="missing"><Icon name="XCircle" size={10} /> Missing</StatusBadge>;
        if (isLowConfidence(field)) return <StatusBadge $status="review"><Icon name="AlertCircle" size={10} /> Needs Review</StatusBadge>;
        return <StatusBadge $status="confirmed"><Icon name="CheckCircle" size={10} /> Confirmed</StatusBadge>;
    };

    return (
        <ReviewWrapper>
            <ConfidenceAlert>
                <Icon name="AlertCircle" size={20} color="#f59e0b" />
                <p>
                    <strong>Extraction Complete:</strong> We've extracted data from your document. Please verify the fields below, especially those flagged for review.
                </p>
            </ConfidenceAlert>

            <ReviewGrid>
                <ReviewField $lowConfidence={isLowConfidence('CustomerName')}>
                    <FieldLabel>
                        Customer Name
                        {renderStatus('CustomerName', data.CustomerName)}
                    </FieldLabel>
                    <FieldValue>{data.CustomerName || '-'}</FieldValue>
                    {isLowConfidence('CustomerName') && (
                        <ConfidenceBadge>
                            {Math.round((data.confidence?.CustomerName || 0) * 100)}% confidence
                        </ConfidenceBadge>
                    )}
                </ReviewField>

                <ReviewField $lowConfidence={isLowConfidence('VendorName')}>
                    <FieldLabel>
                        Vendor Name
                        {renderStatus('VendorName', data.VendorName)}
                    </FieldLabel>
                    <FieldValue>{data.VendorName || '-'}</FieldValue>
                    {isLowConfidence('VendorName') && (
                        <ConfidenceBadge>
                            {Math.round((data.confidence?.VendorName || 0) * 100)}% confidence
                        </ConfidenceBadge>
                    )}
                </ReviewField>

                <ReviewField $lowConfidence={isLowConfidence('Address')} className="address-field">
                    <FieldLabel>
                        Pickup Address
                        {renderStatus('Address', data.customerAddr1)}
                    </FieldLabel>
                    <FieldValue>{data.customerAddr1 || '-'}</FieldValue>
                    {isLowConfidence('Address') && (
                        <ConfidenceBadge>
                            {Math.round((data.confidence?.Address || 0) * 100)}% confidence
                        </ConfidenceBadge>
                    )}
                </ReviewField>
            </ReviewGrid>

            <ReviewActions>
              {onEdit && (
                <Button variant="outline" onClick={onEdit}>
                  {secondaryActionLabel || 'Edit Manually'}
                </Button>
              )}
                <Button onClick={onConfirm}>
                    Confirm & Create Order
                </Button>
            </ReviewActions>
        </ReviewWrapper>
    );
};
